import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Plus, Search, Filter, MoreVertical, Eye, Edit, Trash2, Copy } from 'lucide-react'
import { Button, Badge, Card } from '../../components/ui'
import type { Campaign } from '../../types'

// Mock data
const mockCampaigns: Campaign[] = [
  {
    id: '1',
    advertiserId: 'adv-1',
    name: '春のゲーム祭り',
    description: '春の新作ゲームプロモーション',
    budget: 1000000,
    dailyBudget: 50000,
    spentAmount: 800000,
    bidType: 'cpm',
    bidAmount: 500,
    startDate: '2025-12-01',
    endDate: '2025-12-31',
    status: 'active',
    priority: 1,
    createdAt: '2025-11-15',
    updatedAt: '2025-12-01',
    advertiser: { id: 'adv-1', name: 'ゲーム会社A', status: 'active', createdAt: '', updatedAt: '' },
  },
  {
    id: '2',
    advertiserId: 'adv-2',
    name: '新作タイトルプロモ',
    description: '新作RPGの告知',
    budget: 500000,
    dailyBudget: 25000,
    spentAmount: 200000,
    bidType: 'cpc',
    bidAmount: 50,
    startDate: '2025-12-05',
    endDate: '2025-12-20',
    status: 'active',
    priority: 0,
    createdAt: '2025-11-20',
    updatedAt: '2025-12-01',
    advertiser: { id: 'adv-2', name: 'ゲーム会社B', status: 'active', createdAt: '', updatedAt: '' },
  },
  {
    id: '3',
    advertiserId: 'adv-self',
    name: 'カフェキャンペーン',
    description: 'モーニングコーヒー無料',
    budget: 200000,
    dailyBudget: 20000,
    spentAmount: 0,
    bidType: 'cpm',
    bidAmount: 300,
    startDate: '2025-12-01',
    endDate: '2025-12-15',
    status: 'pending',
    priority: 2,
    createdAt: '2025-11-25',
    updatedAt: '2025-11-28',
    advertiser: { id: 'adv-self', name: '自社', status: 'active', createdAt: '', updatedAt: '' },
  },
  {
    id: '4',
    advertiserId: 'adv-3',
    name: '年末年始セール',
    description: '年末年始の特別セール',
    budget: 800000,
    dailyBudget: 40000,
    spentAmount: 0,
    bidType: 'cpm',
    bidAmount: 400,
    startDate: '2025-12-20',
    endDate: '2026-01-05',
    status: 'draft',
    priority: 0,
    createdAt: '2025-11-28',
    updatedAt: '2025-11-28',
    advertiser: { id: 'adv-3', name: '小売会社C', status: 'active', createdAt: '', updatedAt: '' },
  },
  {
    id: '5',
    advertiserId: 'adv-1',
    name: '秋のイベント',
    description: '秋の大感謝祭',
    budget: 600000,
    dailyBudget: 30000,
    spentAmount: 600000,
    bidType: 'cpm',
    bidAmount: 450,
    startDate: '2025-10-01',
    endDate: '2025-11-30',
    status: 'completed',
    priority: 0,
    createdAt: '2025-09-15',
    updatedAt: '2025-11-30',
    advertiser: { id: 'adv-1', name: 'ゲーム会社A', status: 'active', createdAt: '', updatedAt: '' },
  },
]

const statusConfig = {
  draft: { label: '下書き', variant: 'gray' as const },
  pending: { label: '審査中', variant: 'warning' as const },
  approved: { label: '承認済', variant: 'primary' as const },
  active: { label: '配信中', variant: 'success' as const },
  paused: { label: '停止中', variant: 'danger' as const },
  completed: { label: '終了', variant: 'gray' as const },
}

export default function CampaignListPage() {
  const navigate = useNavigate()
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const filteredCampaigns = mockCampaigns.filter(campaign => {
    const matchesSearch = campaign.name.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesStatus = statusFilter === 'all' || campaign.status === statusFilter
    return matchesSearch && matchesStatus
  })

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    )
  }

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredCampaigns.length) {
      setSelectedIds([])
    } else {
      setSelectedIds(filteredCampaigns.map(c => c.id))
    }
  }

  const getBudgetProgress = (campaign: Campaign) => {
    if (!campaign.budget) return 0
    return Math.round((campaign.spentAmount / campaign.budget) * 100)
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">キャンペーン管理</h1>
          <p className="text-sm text-gray-500 mt-1">広告キャンペーンの作成・管理</p>
        </div>
        <Link to="/campaigns/new">
          <Button leftIcon={<Plus className="w-4 h-4" />}>
            新規キャンペーン作成
          </Button>
        </Link>
      </div>

      {/* Filters */}
      <Card>
        <div className="p-4 flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="キャンペーン名で検索..."
              className="form-input pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div className="flex gap-3">
            <select 
              className="form-select w-40"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">すべてのステータス</option>
              <option value="draft">下書き</option>
              <option value="pending">審査中</option>
              <option value="active">配信中</option>
              <option value="paused">停止中</option>
              <option value="completed">終了</option>
            </select>
            <Button variant="secondary" leftIcon={<Filter className="w-4 h-4" />}>
              詳細フィルタ
            </Button>
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th className="w-10">
                  <input
                    type="checkbox"
                    className="form-checkbox"
                    checked={selectedIds.length === filteredCampaigns.length && filteredCampaigns.length > 0}
                    onChange={toggleSelectAll}
                  />
                </th>
                <th>キャンペーン名</th>
                <th>広告主</th>
                <th>期間</th>
                <th>ステータス</th>
                <th>予算消化</th>
                <th className="w-20">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredCampaigns.map((campaign) => {
                const progress = getBudgetProgress(campaign)
                const status = statusConfig[campaign.status]
                
                return (
                  <tr 
                    key={campaign.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => navigate(`/campaigns/${campaign.id}`)}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="form-checkbox"
                        checked={selectedIds.includes(campaign.id)}
                        onChange={() => toggleSelect(campaign.id)}
                      />
                    </td>
                    <td>
                      <div className="font-medium text-gray-900">{campaign.name}</div>
                      {campaign.description && (
                        <div className="text-sm text-gray-500 truncate max-w-xs">
                          {campaign.description}
                        </div>
                      )}
                    </td>
                    <td className="text-gray-600">{campaign.advertiser?.name}</td>
                    <td className="text-gray-600">
                      <div className="text-sm">
                        {new Date(campaign.startDate).toLocaleDateString('ja-JP')}
                        <span className="mx-1">〜</span>
                        {new Date(campaign.endDate).toLocaleDateString('ja-JP')}
                      </div>
                    </td>
                    <td>
                      <Badge variant={status.variant} dot>
                        {status.label}
                      </Badge>
                    </td>
                    <td>
                      <div className="w-32">
                        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                          <span>{progress}%</span>
                          <span>¥{campaign.spentAmount.toLocaleString()}</span>
                        </div>
                        <div className="w-full bg-gray-100 rounded-full h-2">
                          <div 
                            className={`h-2 rounded-full transition-all ${
                              progress >= 90 ? 'bg-red-500' : 
                              progress >= 70 ? 'bg-yellow-500' : 'bg-green-500'
                            }`}
                            style={{ width: `${progress}%` }}
                          />
                        </div>
                      </div>
                    </td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div className="relative group">
                        <button className="p-2 hover:bg-gray-100 rounded-lg">
                          <MoreVertical className="w-4 h-4 text-gray-500" />
                        </button>
                        <div className="absolute right-0 mt-1 w-40 bg-white rounded-lg shadow-lg border border-gray-200 py-1 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-10">
                          <button 
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                            onClick={() => navigate(`/campaigns/${campaign.id}`)}
                          >
                            <Eye className="w-4 h-4 mr-2" />
                            詳細
                          </button>
                          <button 
                            className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center"
                            onClick={() => navigate(`/campaigns/${campaign.id}/edit`)}
                          >
                            <Edit className="w-4 h-4 mr-2" />
                            編集
                          </button>
                          <button className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center">
                            <Copy className="w-4 h-4 mr-2" />
                            複製
                          </button>
                          <button className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center">
                            <Trash2 className="w-4 h-4 mr-2" />
                            削除
                          </button>
                        </div>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            全{filteredCampaigns.length}件中 1〜{filteredCampaigns.length}件を表示
          </div>
          <div className="flex items-center space-x-2">
            <button className="pagination-btn rounded-l-md" disabled>
              前へ
            </button>
            <button className="pagination-btn pagination-btn-active">1</button>
            <button className="pagination-btn rounded-r-md" disabled>
              次へ
            </button>
          </div>
        </div>
      </Card>

      {/* Bulk Actions */}
      {selectedIds.length > 0 && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white px-6 py-3 rounded-lg shadow-lg flex items-center gap-4 animate-fadeIn">
          <span className="text-sm">{selectedIds.length}件選択中</span>
          <div className="flex gap-2">
            <Button size="sm" variant="ghost" className="text-white hover:bg-gray-800">
              一括開始
            </Button>
            <Button size="sm" variant="ghost" className="text-white hover:bg-gray-800">
              一括停止
            </Button>
            <Button size="sm" variant="ghost" className="text-red-400 hover:bg-gray-800">
              一括削除
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

